import data from "@/public/data/flight/passengers.json";
import {
  Airplane,
  AirplaneLanding,
  AirplaneTakeoff,
  AirTrafficControl,
  CalendarBlank,
} from "@phosphor-icons/react/dist/ssr";
import Typography from "@/shared/components/common/Typography";
import Flex from "@/shared/components/common/Flex";
import Image from "next/image";
import staticData from "@/public/data/flight/results.json";
import React from "react";
import Separator from "@/shared/components/common/separator";
import { Button } from "@/shared/components/common/button";
import { useRouter } from "next/navigation";
import { cn } from "@/shared/libs/commonUtils";
import FlightCounterTimer from "../components/FlightCounterTimer";
import InfoCard from "@/containers/components/cards/infoCard/InfoCard";
import { useDeviceType } from "@/shared/context/DeviceContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/common/Accordion";
import { Suitcase, WarningCircle } from "@phosphor-icons/react";

interface FlightInfoCardProps {
  searchParams: SearchParams;
  flightDetail: FlightDetail;
  refundRules: RefundRule[];
}

export default function FlightInfoCard({
  flightDetail,
  refundRules,
}: FlightInfoCardProps) {
  const router = useRouter();
  const deviceType = useDeviceType();
  const [failedAirlineLogos, setFailedAirlineLogos] = React.useState<
    Set<string>
  >(() => new Set());

  const {
    flightInfo: { title, departTicket, returnTicket },
  } = data;
  const { resultCard } = staticData;

  const flights = Object.values(flightDetail);

  const changeFlightButton = (
    <Button
      disabled
      variant="outline"
      className="md:w-full py-6 max-md:py-2 hover:bg-third shadow-1 hover:shadow-2"
      onClick={() => router.back()}
    >
      <Typography className="font-medium">
        {resultCard.changeFlightLabel}
      </Typography>
    </Button>
  );

  return (
    <InfoCard
      header={{
        title,
        icon: <Airplane weight="fill" className="w-5 h-5 text-secondary" />,
        button: <FlightCounterTimer />,
      }}
      defaultStyle={3}
    >
      <Flex className="flex-col gap-4">
        {flights.map(
          (
            {
              price,
              departure_city_name_lang,
              arrival_city_name_lang,
              airline_logo,
              airline_name_lang,
              arrival_time,
              departure_time,
              jalali_date,
              flight_no,
              type_lang,
              airline_code,
              airline_name,
              baggage,
              cabin_type,
            },
            z,
          ) => (
            <Flex
              key={airline_code}
              className={cn(
                flights.length > 1 ? "basis-1/2 " : "w-full",
                "flex-col md:border border-border rounded-xs py-3 px-3 md:py-3",
              )}
            >
              <Flex className="w-full max-md:flex-col">
                <Flex className="md:w-[70%] max-md:w-full flex-col">
                {/* Tags */}
                <Flex className="gap-x-3 items-center justify-between">
                  <Flex className="items-center justify-center gap-x-2 md:hidden">
                    {airline_logo &&
                    airline_logo !== "no logo" &&
                    !failedAirlineLogos.has(airline_logo) ? (
                      <Image
                        src={airline_logo}
                        width={40}
                        height={40}
                        alt="airline"
                        className="w-12 h-12"
                        onError={() =>
                          setFailedAirlineLogos(
                            (failedLogos) =>
                              new Set(failedLogos).add(airline_logo),
                          )
                        }
                      />
                    ) : (
                      <AirTrafficControl className="w-6 h-6 text-secondary" />
                    )}

                    <Flex className="flex-col gap-1">
                      <Flex className="gap-2">
                        <Typography variant="body1" className=" font-normal">
                          {airline_name_lang}
                        </Typography>
                        {flights.length > 1 && (
                          <Flex className="w-max bg-success-10 py-1 px-2 rounded-md items-center">
                            <Typography className="text-sm leading-none! text-success">
                              {z === 0 ? departTicket : returnTicket}
                            </Typography>
                          </Flex>
                        )}
                      </Flex>
                      <Flex className="gap-1">
                        <Typography className="text-muted-foreground text-xs">
                          {type_lang} -
                        </Typography>
                        <Typography className="text-muted-foreground text-xs">
                          {flight_no}
                        </Typography>
                      </Flex>
                    </Flex>
                  </Flex>
                  <Flex className="gap-1 max-md:hidden">
                    <Typography className="text-muted-foreground">
                      {jalali_date}
                    </Typography>
                  </Flex>
                  {deviceType === "mobile" && changeFlightButton}
                </Flex>

                {/* Body */}

                <Flex className="max-md:flex-col p-4 gap-x-8 gap-y-4 justify-center">
                  <Flex className="basis-[10%] max-md:basis-1 flex-col items-center justify-center max-md:hidden">
                    {airline_logo &&
                    airline_logo !== "no logo" &&
                    !failedAirlineLogos.has(airline_logo) ? (
                      <Image
                        src={airline_logo}
                        width={40}
                        height={40}
                        alt="airline"
                        className="w-12 h-12"
                        onError={() =>
                          setFailedAirlineLogos(
                            (failedLogos) =>
                              new Set(failedLogos).add(airline_logo),
                          )
                        }
                      />
                    ) : (
                      <AirTrafficControl className="w-6 h-6 text-secondary" />
                    )}

                    <Typography
                      variant="body1"
                      className="font-normal text-muted-foreground"
                    >
                      {airline_name_lang}
                    </Typography>
                  </Flex>

                  <Flex
                    className={
                      "basis-[90%] max-md:basis-1 gap-x-4 items-center justify-between"
                    }
                  >
                    <Flex className="flex-col items-center">
                      <Typography variant="h5">{departure_time}</Typography>
                      <Typography variant="h6" className="text-ghost">
                        {departure_city_name_lang}
                      </Typography>
                    </Flex>

                    <Flex className="w-full items-center justify-between max-md:relative">
                      <AirplaneTakeoff
                        weight="fill"
                        className="w-5 h-5 md:w-8 md:h-8 text-primary max-md:absolute max-md:-top-4 max-md:right-1 scale-x-[-1]"
                      />
                      <Flex className="w-full! md:w-[80%]! flex-col items-center md:gap-y-2">
                        <Typography className="max-md:hidden">
                          {type_lang}
                        </Typography>
                        <Separator
                          direction="horizontal"
                          className="h-px! md:w-[80%] border-b max-md:border-dashed md:h-[2px]! text-slate-700"
                        />
                        <Typography className="max-md:hidden">
                          {flight_no}
                        </Typography>
                      </Flex>
                      <AirplaneLanding
                        weight="fill"
                        className="w-5 h-5 md:w-8 md:h-8 text-primary max-md:absolute max-md:-top-4  max-md:left-1 scale-x-[-1]"
                      />
                    </Flex>

                    <Flex className="flex-col items-center">
                      <Typography variant="h5">{arrival_time}</Typography>
                      <Typography variant="h6" className="">
                        {arrival_city_name_lang}
                      </Typography>
                    </Flex>
                  </Flex>
                </Flex>
                </Flex>
                <Separator
                  direction="horizontal"
                  className="md:hidden border-b border-double mb-4 h-px! md:h-[2px]! text-slate-700"
                />
                <Flex className="md:w-[40%]! max-md:w-full md:p-3 max-md:pb-0 flex-col justify-between md:justify-center md:items-center md:h-full md:gap-y-4 md:py-2 bg-transparent max-md:gap-y-2">
                  <Flex className="justify-between items-center">
                    <Flex className="px-3">
                      <Flex className="gap-1 md:hidden">
                        <CalendarBlank className="w-5 h-5 text-muted-foreground md:hidden" />
                        <Typography>{jalali_date}</Typography>
                      </Flex>
                    </Flex>
                    <Flex className="gap-x-1 items-center">
                      <Typography
                        variant="h6"
                        className="text-xl font-semibold text-third"
                      >
                        {price.toLocaleString()}
                      </Typography>
                      <Typography className="text-md font-normal text-ghost">
                        {resultCard.currencyLabel}
                      </Typography>
                    </Flex>
                  </Flex>

                  {deviceType === "desktop" && changeFlightButton}
                </Flex>
              </Flex>

              <FlightPoliciesAccordion
                flightKey={airline_code || flight_no || String(z)}
                airlineName={airline_name_lang || airline_name}
                baggage={baggage}
                cabinType={cabin_type}
                refundRule={findRefundRule(
                  refundRules,
                  airline_name_lang || airline_name,
                )}
              />
            </Flex>
          ),
        )}
      </Flex>
    </InfoCard>
  );
}

interface FlightPoliciesAccordionProps {
  flightKey: string;
  airlineName: string;
  baggage: FlightBaggage;
  cabinType: string;
  refundRule?: RefundRule;
}

function FlightPoliciesAccordion({
  flightKey,
  airlineName,
  baggage,
  cabinType,
  refundRule,
}: FlightPoliciesAccordionProps) {
  const baggageItems = [
    { label: "بزرگسال", value: baggage?.adult },
    { label: "کودک", value: baggage?.adult },
    { label: "نوزاد", value: baggage?.infant },
  ].filter(({ value }) => value !== null && value !== undefined);

  return (
    <Accordion type="single" collapsible className="w-full border-t border-border">
      <AccordionItem value={`policies-${flightKey}`} className="border-none!">
        <AccordionTrigger className="w-full py-3 text-start hover:no-underline">
          <Flex className="items-center gap-2">
            <Suitcase weight="fill" className="h-5 w-5 text-primary" />
            <Typography className="font-medium">قوانین کنسلی و بار مجاز</Typography>
          </Flex>
        </AccordionTrigger>
        <AccordionContent className="px-0 pb-1 pt-0">
          <Flex className="flex-col gap-4">
            <Flex className="flex-col gap-2 rounded-md bg-muted p-3">
              <Typography className="font-medium">بار مجاز</Typography>
              <Flex className="flex-wrap gap-2">
                {baggageItems.length > 0 ? (
                  baggageItems.map(({ label, value }) => (
                    <Flex
                      key={label}
                      className="items-center gap-1 rounded-sm bg-background px-2 py-1"
                    >
                      <Typography variant="caption" className="text-muted-foreground">
                        {label}
                      </Typography>
                      <Typography variant="caption" className="font-medium">
                        {value} کیلوگرم
                      </Typography>
                    </Flex>
                  ))
                ) : (
                  <Typography variant="caption" className="text-muted-foreground">
                    میزان بار مجاز توسط ایرلاین اعلام نشده است.
                  </Typography>
                )}
              </Flex>
            </Flex>

            <Flex className="flex-col gap-2">
              <Typography className="font-medium">قوانین کنسلی</Typography>
              {refundRule ? (
                <Flex className="flex-col gap-2">
                  <Typography variant="caption" className="text-muted-foreground">
                    درصدهای زیر جریمه استرداد بلیط {airlineName} هستند.
                  </Typography>
                  {refundRule.data.map(({ rate_id, data }) => (
                    <Flex
                      key={rate_id || "all-rates"}
                      className="flex-col gap-2 rounded-md border border-border p-3"
                    >
                      <Typography variant="caption" className="font-medium">
                        {rate_id ? `کلاس نرخی ${rate_id}` : "تمامی کلاس‌های نرخی"}
                        {cabinType ? ` (${cabinType})` : ""}
                      </Typography>
                      <Flex className="flex-wrap gap-x-4 gap-y-2">
                        {refundRule.descriptions.map((description, index) => (
                          <Typography key={description} variant="caption">
                            {description}: {data[index] ? `${data[index]}٪` : "—"}
                          </Typography>
                        ))}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Flex className="items-start gap-2 rounded-md bg-muted p-3">
                  <WarningCircle weight="fill" className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <Typography variant="caption" className="text-muted-foreground">
                    قوانین کنسلی این ایرلاین در حال حاضر در دسترس نیست. برای اطلاع از
                    شرایط استرداد با پشتیبانی تماس بگیرید.
                  </Typography>
                </Flex>
              )}
            </Flex>
          </Flex>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function findRefundRule(rules: RefundRule[], airlineName: string) {
  const normalizedAirlineName = normalizeAirlineName(airlineName);

  return rules.find((rule) => {
    const normalizedRuleName = normalizeAirlineName(rule.airline);
    return (
      normalizedRuleName === normalizedAirlineName ||
      normalizedRuleName.includes(normalizedAirlineName) ||
      normalizedAirlineName.includes(normalizedRuleName)
    );
  });
}

function normalizeAirlineName(name: string) {
  return name
    .replace(/[يى]/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/شرکت|هواپیمایی|ایرلاین|airlines?|airways?/gi, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();
}
